import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Checkbox,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import TimelineIcon from "@mui/icons-material/Timeline";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import DashboardLayout from "../../layouts/DashboardLayout";
import MDBox from "../../components/MDBox";
import MDTypography from "../../components/MDTypography";
import { forecastApi } from "../../services/api/forecast";

const METRICS = [
  { key: "forecast_qty", label: "Units" },
  { key: "forecast_order_count", label: "Orders" },
  { key: "forecast_revenue", label: "Revenue" },
  { key: "forecast_profit", label: "Profit" },
];

const PLATFORM_IDS = {
  plenty: "plenty__ALL",
  amazon: "amazon__ALL",
};

/** Human-readable explanation of each forecast series / grain */
const SERIES_EXPLAIN = {
  plenty: {
    platform: {
      title: "Platform series",
      body: "One series for all Plenty sales per day (unique_id plenty__ALL). This is the headline total — not a sum of SKU forecasts.",
    },
    channel: {
      title: "Channel (origin) series",
      body: "One series per Plenty origin (Otto, Shopify, Amazon, …). Choose an origin — that loads that channel’s 30-day forecast (e.g. plenty__Otto).",
    },
    product: {
      title: "Product series",
      body: "One series per product_key (Plenty asin_sku) × origin × ship-to country. Search by ASIN/SKU (product_key), optionally narrow by origin/country, then pick the matching series if needed.",
    },
  },
  amazon: {
    platform: {
      title: "Platform series",
      body: "One series for all Amazon sales per day (unique_id amazon__ALL). Headline marketplace total — not a rollup of ASINs.",
    },
    marketplace: {
      title: "Marketplace series",
      body: "One series per marketplace (DE, IT, FR, …). Choose a marketplace — that loads that country’s Amazon forecast (e.g. amazon__DE).",
    },
    product: {
      title: "Product series",
      body: "One series per product_key (ASIN, or seller SKU if ASIN is empty) × marketplace. Search by ASIN/SKU, then pick the marketplace row if needed.",
    },
  },
};

const formatSeriesLabel = (s, grain) => {
  if (grain === "product" && s.product_key) {
    const dims = [s.channel, s.country, s.marketplace]
      .filter(Boolean)
      .join(" · ");
    const title = s.title ? ` — ${s.title}` : "";
    return dims
      ? `${s.product_key} (${dims})${title}`
      : `${s.product_key}${title}`;
  }
  if (grain === "channel" && s.channel) return s.channel;
  if (grain === "marketplace" && s.marketplace) return s.marketplace;
  return s.unique_id;
};

const statusColor = (s) => {
  if (s === "success") return "success";
  if (s === "running") return "warning";
  if (s === "error") return "error";
  return "default";
};

const formatTs = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const formatNum = (n, digits = 1) =>
  Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: digits });

/** e.g. "2–3d" or "3d" when min==max; falls back to point days. */
const formatOosDaysRange = (row) => {
  if (!row) return null;
  const dMin = row.days_until_oos_min ?? row.days_until_oos;
  const dMax = row.days_until_oos_max ?? row.days_until_oos;
  if (dMin == null && dMax == null) return null;
  if (dMin != null && dMax != null && Number(dMin) !== Number(dMax)) {
    return `${dMin}–${dMax}d`;
  }
  return `${dMin ?? dMax}d`;
};

const formatOosChipLabel = (row) => {
  const days = formatOosDaysRange(row);
  if (!row?.oos_date && !days) return null;
  if (
    row.oos_date_min &&
    row.oos_date_max &&
    row.oos_date_min !== row.oos_date_max
  ) {
    return `OOS ${row.oos_date_min}…${row.oos_date_max}${
      days ? ` (~${days})` : ""
    }`;
  }
  if (row.oos_date) {
    return `OOS ${row.oos_date}${days ? ` (~${days})` : ""}`;
  }
  return days ? `OOS in ~${days}` : null;
};

const ForecastPage = () => {
  const [platform, setPlatform] = useState("plenty");
  const [metric, setMetric] = useState("forecast_qty");
  const [granularity, setGranularity] = useState("platform");
  const [uniqueId, setUniqueId] = useState(PLATFORM_IDS.plenty);
  const [viewportH, setViewportH] = useState(() =>
    typeof window !== "undefined" ? window.innerHeight : 900,
  );
  const [seriesOptions, setSeriesOptions] = useState([]);
  const [points, setPoints] = useState([]);
  const [status, setStatus] = useState(null);
  const [dimensions, setDimensions] = useState({
    plenty_channels: [],
    plenty_countries: [],
    amazon_marketplaces: [],
  });
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [productSearch, setProductSearch] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [marketplaceFilter, setMarketplaceFilter] = useState("");
  const [stockout, setStockout] = useState(null);
  const [stockoutLoading, setStockoutLoading] = useState(false);
  const [stockoutScope, setStockoutScope] = useState("product");

  // OOS Explorer sub-tab (next to Plenty/Amazon)
  const [oosSource, setOosSource] = useState("both"); // plenty | amazon | both
  const [oosQuery, setOosQuery] = useState("");
  const [oosSelectedKey, setOosSelectedKey] = useState("");
  const [oosSuggestions, setOosSuggestions] = useState([]);
  const [oosCandidates, setOosCandidates] = useState({
    items: [],
    graph: { dates: [], counts: [] },
  });
  const [oosDetail, setOosDetail] = useState(null);
  const [oosDetailLoading, setOosDetailLoading] = useState(false);
  const isOosTab = platform === "oos";
  const isOpsTab = platform === "ops";

  // Ops accuracy (MAPE) tab
  const [opsMetric, setOpsMetric] = useState("qty");
  const [opsDays, setOpsDays] = useState(30);
  const [opsReport, setOpsReport] = useState(null);
  const [opsLoading, setOpsLoading] = useState(false);
  const [opsRefresh, setOpsRefresh] = useState(0);
  const [hpoItems, setHpoItems] = useState([]);
  const [opsJobMsg, setOpsJobMsg] = useState(null);
  const [tuneSource, setTuneSource] = useState("all"); // all | plenty | amazon
  const [tuneTrials, setTuneTrials] = useState(30);
  const [tuneFolds, setTuneFolds] = useState(4);
  const [tuneStepDays, setTuneStepDays] = useState(30);
  const [tuneMetrics, setTuneMetrics] = useState(["qty", "orders"]);
  const [tuneExcludeRecent, setTuneExcludeRecent] = useState(1);
  const [tuneHoldoutDays, setTuneHoldoutDays] = useState(0);
  const tuneDefaultsSeeded = useRef(false);

  useEffect(() => {
    const onResize = () => setViewportH(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const grainChoices = useMemo(() => {
    if (platform === "plenty") {
      return [
        { value: "platform", label: "Platform total" },
        { value: "channel", label: "Channel (origin)" },
        { value: "product", label: "Product (ASIN / SKU)" },
      ];
    }
    return [
      { value: "platform", label: "Platform total" },
      { value: "marketplace", label: "Marketplace" },
      { value: "product", label: "Product (ASIN / SKU)" },
    ];
  }, [platform]);

  const seriesHelp = SERIES_EXPLAIN[platform]?.[granularity];

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await forecastApi.getStatus();
      setStatus(data);
      return data;
    } catch (e) {
      return null;
    }
  }, []);

  // Seed Ops tune form from server defaults once
  useEffect(() => {
    if (!status || tuneDefaultsSeeded.current) return;
    tuneDefaultsSeeded.current = true;
    if (status.hpo_trials != null) setTuneTrials(Number(status.hpo_trials));
    if (status.hpo_folds != null) setTuneFolds(Number(status.hpo_folds));
    if (status.hpo_step_days != null)
      setTuneStepDays(Number(status.hpo_step_days));
    if (status.hpo_exclude_recent_days != null)
      setTuneExcludeRecent(Number(status.hpo_exclude_recent_days));
    if (status.hpo_holdout_days != null)
      setTuneHoldoutDays(Number(status.hpo_holdout_days));
    if (status.hpo_metrics) {
      const parts = String(status.hpo_metrics)
        .replace(/,/g, " ")
        .split(/\s+/)
        .map((m) => m.trim().toLowerCase())
        .filter((m) => ["qty", "orders", "revenue", "profit"].includes(m));
      if (parts.length) setTuneMetrics(parts);
    }
  }, [status]);

  const tuneExtras = useMemo(
    () => ({
      n_trials: tuneTrials,
      n_folds: tuneFolds,
      step_days: tuneStepDays,
      metrics: tuneMetrics.join(","),
      exclude_recent_days: Math.max(0, Number(tuneExcludeRecent) || 0),
      holdout_days: Math.max(0, Number(tuneHoldoutDays) || 0),
    }),
    [
      tuneTrials,
      tuneFolds,
      tuneStepDays,
      tuneMetrics,
      tuneExcludeRecent,
      tuneHoldoutDays,
    ],
  );

  const backtestExtras = useMemo(
    () => ({
      n_folds: tuneFolds,
      step_days: tuneStepDays,
      metrics: tuneMetrics.join(","),
      exclude_recent_days: Math.max(0, Number(tuneExcludeRecent) || 0),
    }),
    [tuneFolds, tuneStepDays, tuneMetrics, tuneExcludeRecent],
  );

  const loadDimensions = useCallback(async () => {
    try {
      const { data } = await forecastApi.getDimensions();
      setDimensions({
        plenty_channels: data.plenty_channels || [],
        plenty_countries: data.plenty_countries || [],
        amazon_marketplaces: data.amazon_marketplaces || [],
      });
    } catch (e) {
      // non-critical
    }
  }, []);

  const loadSeriesOptions = useCallback(async () => {
    try {
      if (isOosTab || isOpsTab) {
        setSeriesOptions([]);
        return;
      }
      // Channel / marketplace: series identity = origin / marketplace picker (no list needed)
      if (granularity === "channel" || granularity === "marketplace") {
        setSeriesOptions([]);
        return;
      }
      // Product grain: require ASIN/SKU (product_key) search
      if (granularity === "product" && !productSearch.trim()) {
        setSeriesOptions([]);
        return;
      }
      const params = { granularity };
      if (platform === "plenty") {
        if (channelFilter) params.channel = channelFilter;
        if (countryFilter) params.country = countryFilter;
        if (productSearch.trim()) params.product_key = productSearch.trim();
        const { data } = await forecastApi.plentySeries(params);
        setSeriesOptions(data.items || []);
      } else {
        if (marketplaceFilter) params.marketplace = marketplaceFilter;
        if (productSearch.trim()) params.product_key = productSearch.trim();
        const { data } = await forecastApi.amazonSeries(params);
        setSeriesOptions(data.items || []);
      }
    } catch (e) {
      setSeriesOptions([]);
    }
  }, [
    platform,
    granularity,
    channelFilter,
    countryFilter,
    marketplaceFilter,
    productSearch,
  ]);

  const loadForecast = useCallback(async () => {
    if (isOosTab || isOpsTab) {
      setPoints([]);
      return;
    }
    if (!uniqueId) {
      setPoints([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = {
        granularity,
        unique_id: uniqueId,
        limit: 500,
      };
      const { data } =
        platform === "plenty"
          ? await forecastApi.listPlenty(params)
          : await forecastApi.listAmazon(params);
      setPoints(data.items || []);
    } catch (e) {
      setError(
        e.response?.data?.detail || e.message || "Failed to load forecast data",
      );
      setPoints([]);
    } finally {
      setLoading(false);
    }
  }, [platform, granularity, uniqueId]);

  const loadStockout = useCallback(async () => {
    if (isOosTab || isOpsTab) {
      setStockout(null);
      return;
    }
    if (granularity !== "product") {
      setStockout(null);
      return;
    }
    const key =
      seriesOptions.find((s) => s.unique_id === uniqueId)?.product_key ||
      productSearch.trim();
    if (!key) {
      setStockout(null);
      return;
    }
    setStockoutLoading(true);
    try {
      const params = {
        source: platform,
        product_key: key,
        scope: stockoutScope,
      };
      if (stockoutScope === "series" && uniqueId) {
        params.unique_id = uniqueId;
      }
      const { data } = await forecastApi.stockout(params);
      setStockout(data);
    } catch (e) {
      setStockout({
        message:
          e.response?.data?.detail ||
          e.message ||
          "Failed to load stockout projection",
        points: [],
      });
    } finally {
      setStockoutLoading(false);
    }
  }, [
    granularity,
    platform,
    uniqueId,
    productSearch,
    seriesOptions,
    stockoutScope,
  ]);

  useEffect(() => {
    loadStatus();
    loadDimensions();
  }, [loadStatus, loadDimensions]);

  // Poll while a run is in progress
  useEffect(() => {
    if (!status?.running && status?.sources?.[platform]?.status !== "running") {
      return undefined;
    }
    const id = setInterval(async () => {
      const next = await loadStatus();
      if (next && !next.running) {
        loadDimensions();
        loadSeriesOptions();
        loadForecast();
      }
    }, 4000);
    return () => clearInterval(id);
  }, [
    status?.running,
    status?.sources,
    platform,
    loadStatus,
    loadDimensions,
    loadSeriesOptions,
    loadForecast,
  ]);

  useEffect(() => {
    setGranularity("platform");
    setUniqueId(PLATFORM_IDS[platform]);
    setChannelFilter("");
    setCountryFilter("");
    setMarketplaceFilter("");
    setProductSearch("");
    setSeriesOptions([]);
    setPoints([]);
  }, [platform]);

  // Only when series type changes — do not re-run when filters recreate loadSeriesOptions
  useEffect(() => {
    if (granularity === "platform") {
      setUniqueId(PLATFORM_IDS[platform]);
      setChannelFilter("");
      setMarketplaceFilter("");
      setProductSearch("");
      setSeriesOptions([]);
      return;
    }
    if (granularity === "channel" || granularity === "marketplace") {
      setUniqueId("");
      setChannelFilter("");
      setMarketplaceFilter("");
      setProductSearch("");
      setSeriesOptions([]);
      setPoints([]);
      return;
    }
    // product
    setUniqueId("");
    setProductSearch("");
    setSeriesOptions([]);
    setPoints([]);
  }, [granularity, platform]);

  useEffect(() => {
    if (granularity === "product") {
      loadSeriesOptions();
    }
  }, [
    granularity,
    channelFilter,
    countryFilter,
    marketplaceFilter,
    productSearch,
    loadSeriesOptions,
  ]);

  useEffect(() => {
    // Product: auto-pick first match after ASIN/SKU search
    if (granularity !== "product") return;
    if (!productSearch.trim()) return;
    if (!uniqueId && seriesOptions.length > 0) {
      setUniqueId(seriesOptions[0].unique_id);
    }
    // If current selection dropped out of filtered results, re-pick
    if (
      uniqueId &&
      seriesOptions.length > 0 &&
      !seriesOptions.some((s) => s.unique_id === uniqueId)
    ) {
      setUniqueId(seriesOptions[0].unique_id);
    }
  }, [seriesOptions, granularity, uniqueId, productSearch]);

  useEffect(() => {
    if (uniqueId) loadForecast();
    else setPoints([]);
  }, [uniqueId, loadForecast]);

  useEffect(() => {
    loadStockout();
  }, [loadStockout]);

  // Ops accuracy report + stored HPO params
  useEffect(() => {
    if (!isOpsTab) return;
    let cancelled = false;
    (async () => {
      setOpsLoading(true);
      try {
        const [{ data }, hpoRes] = await Promise.all([
          forecastApi.accuracy({
            source: "both",
            days: opsDays,
            metric: opsMetric,
            granularity: "platform",
          }),
          forecastApi.getHpo({ source: "both" }),
        ]);
        if (!cancelled) {
          setOpsReport(data);
          setHpoItems(hpoRes?.data?.items || []);
        }
      } catch (e) {
        if (!cancelled) {
          setOpsReport(null);
          setHpoItems([]);
          setError(
            e.response?.data?.detail ||
              e.message ||
              "Failed to load accuracy report",
          );
        }
      } finally {
        if (!cancelled) setOpsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpsTab, opsDays, opsMetric, opsRefresh]);

  // OOS Explorer: keep suggestions + candidates up to date while typing.
  useEffect(() => {
    if (!isOosTab) return;
    const q = oosQuery.trim();

    const timeoutId = setTimeout(async () => {
      try {
        if (q) {
          const { data: suggestData } = await forecastApi.stockoutSuggest({
            source: oosSource,
            query: q,
            limit: 8,
          });
          setOosSuggestions(suggestData.items || []);
        } else {
          setOosSuggestions([]);
        }

        const { data: candidatesData } = await forecastApi.oosCandidates({
          source: oosSource,
          query: q,
          limit: 10000,
        });
        setOosCandidates({
          items: candidatesData.items || [],
          graph: candidatesData.graph || {
            dates: [],
            counts: [],
          },
        });
      } catch {
        setOosSuggestions([]);
        setOosCandidates({ items: [], graph: { dates: [], counts: [] } });
      }
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [isOosTab, oosQuery, oosSource]);

  // OOS Explorer: load the per-key projection when user picks a key.
  useEffect(() => {
    if (!isOosTab) return;
    const key = oosSelectedKey.trim();
    if (!key) {
      setOosDetail(null);
      return;
    }

    const load = async () => {
      setOosDetailLoading(true);
      try {
        if (oosSource === "plenty") {
          const { data } = await forecastApi.stockout({
            source: "plenty",
            product_key: key,
            scope: "product",
          });
          setOosDetail(data);
          return;
        }
        if (oosSource === "amazon") {
          const { data } = await forecastApi.stockout({
            source: "amazon",
            product_key: key,
            scope: "product",
          });
          setOosDetail(data);
          return;
        }

        // Both: prefer the projection that actually matches stock first for
        // the *forecast* part, but combine actual sales (last 30d) across both.
        const [{ data: plentyData }, { data: amazonData }] = await Promise.all([
          forecastApi.stockout({
            source: "plenty",
            product_key: key,
            scope: "product",
          }),
          forecastApi.stockout({
            source: "amazon",
            product_key: key,
            scope: "product",
          }),
        ]);

        const pickBest = (a, b) => {
          const aOk =
            a?.article?.stock !== undefined && a?.article?.stock !== null;
          const bOk =
            b?.article?.stock !== undefined && b?.article?.stock !== null;
          if (aOk && !bOk) return a;
          if (bOk && !aOk) return b;

          // If both match, pick earliest oos_date.
          if (a?.oos_date && b?.oos_date) {
            return String(a.oos_date).localeCompare(String(b.oos_date)) <= 0
              ? a
              : b;
          }
          return a?.oos_date ? a : b;
        };

        const best = pickBest(plentyData, amazonData);

        const sumActualSalesByDs = (rows) => {
          const m = new Map();
          for (const r of rows || []) {
            const ds = r?.ds;
            if (!ds) continue;
            const cur = m.get(ds) || 0;
            m.set(ds, cur + Number(r?.actual_qty ?? 0));
          }
          return m;
        };

        const plentyMap = sumActualSalesByDs(plentyData?.actual_sales);
        const amazonMap = sumActualSalesByDs(amazonData?.actual_sales);

        const combinedMap = new Map(plentyMap);
        for (const [ds, qty] of amazonMap.entries()) {
          combinedMap.set(ds, (combinedMap.get(ds) || 0) + qty);
        }

        const combinedActualSales = Array.from(combinedMap.entries())
          .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
          .map(([ds, qty]) => ({ ds, actual_qty: qty }));

        setOosDetail({ ...best, actual_sales: combinedActualSales });
      } catch {
        setOosDetail(null);
      } finally {
        setOosDetailLoading(false);
      }
    };

    load();
  }, [isOosTab, oosSelectedKey, oosSource]);

  const handleRun = async (source, { hpo } = {}) => {
    const src = source === "both" ? "all" : source;
    setRunning(true);
    setError(null);
    setOpsJobMsg(null);
    try {
      await forecastApi.run(src, false, hpo ?? null);
      setOpsJobMsg(
        hpo
          ? "HPO + forecast started in the background (can take a long time)."
          : "Forecast started in the background.",
      );
      await loadStatus();
    } catch (e) {
      setError(
        e.response?.data?.detail || e.message || "Failed to start forecast run",
      );
    } finally {
      setRunning(false);
    }
  };

  const handleBacktest = async () => {
    setRunning(true);
    setError(null);
    setOpsJobMsg(null);
    try {
      await forecastApi.runBacktest(tuneSource, false, backtestExtras);
      const srcLabel =
        tuneSource === "all"
          ? "Plenty + Amazon"
          : tuneSource === "plenty"
            ? "Plenty only"
            : "Amazon only";
      setOpsJobMsg(
        `Walk-forward backtest started (${srcLabel}) — excludes last ${backtestExtras.exclude_recent_days} days; fills Ops MAPE for ${tuneMetrics.join(" + ")}. Refresh when the job finishes.`,
      );
      await loadStatus();
    } catch (e) {
      setError(
        e.response?.data?.detail || e.message || "Failed to start backtest",
      );
    } finally {
      setRunning(false);
    }
  };

  const handleHpo = async () => {
    setRunning(true);
    setError(null);
    setOpsJobMsg(null);
    try {
      await forecastApi.runHpo(tuneSource, false, tuneExtras);
      const srcLabel =
        tuneSource === "all"
          ? "Plenty + Amazon"
          : tuneSource === "plenty"
            ? "Plenty only"
            : "Amazon only";
      setOpsJobMsg(
        `Optuna HPO started (${srcLabel}) — ${tuneExtras.n_trials} trials × ${tuneExtras.n_folds} folds, exclude last ${tuneExtras.exclude_recent_days}d, hold-out ${tuneExtras.holdout_days}d. CPU-heavy; refresh when done.`,
      );
      await loadStatus();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to start HPO");
    } finally {
      setRunning(false);
    }
  };

  const chartData = useMemo(() => {
    const sorted = [...points].sort((a, b) =>
      String(a.ds).localeCompare(String(b.ds)),
    );
    return {
      dates: sorted.map((p) => p.ds),
      values: sorted.map((p) => Number(p[metric] ?? 0)),
    };
  }, [points, metric]);

  const stockoutChart = useMemo(() => {
    const pts = stockout?.points || [];
    return {
      dates: pts.map((p) => p.ds),
      remaining: pts.map((p) => Number(p.remaining_stock ?? 0)),
      demand: pts.map((p) => Number(p.forecast_qty ?? 0)),
    };
  }, [stockout]);

  const totals = useMemo(() => {
    const sum = (key) =>
      points.reduce((acc, p) => acc + Number(p[key] || 0), 0);
    return {
      qty: sum("forecast_qty"),
      orders: sum("forecast_order_count"),
      revenue: sum("forecast_revenue"),
      profit: sum("forecast_profit"),
    };
  }, [points]);

  const sourceStatus = status?.sources?.[platform];
  const otherPlatform = platform === "plenty" ? "amazon" : "plenty";
  const otherStatus = status?.sources?.[otherPlatform];
  const selectedSeries =
    seriesOptions.find((s) => s.unique_id === uniqueId) ||
    (granularity === "channel" && channelFilter
      ? {
          unique_id: `plenty__${channelFilter}`,
          channel: channelFilter,
          country: "ALL",
          product_key: null,
        }
      : null) ||
    (granularity === "marketplace" && marketplaceFilter
      ? {
          unique_id: `amazon__${marketplaceFilter}`,
          marketplace: marketplaceFilter,
          product_key: null,
        }
      : null);
  const dataPointsUsed =
    points.find((p) => p.data_points_used != null)?.data_points_used ?? null;
  const modelRunAt =
    points.find((p) => p.model_run_at)?.model_run_at ||
    sourceStatus?.model_run_at;

  const jobBusy = Boolean(status?.running || running);

  const onPickOrigin = (origin) => {
    setChannelFilter(origin);
    if (granularity === "channel") {
      setUniqueId(origin ? `plenty__${origin}` : "");
    } else {
      setUniqueId("");
    }
  };

  const onPickMarketplace = (mp) => {
    setMarketplaceFilter(mp);
    if (granularity === "marketplace") {
      setUniqueId(mp ? `amazon__${mp}` : "");
    } else {
      setUniqueId("");
    }
  };

  if (isOpsTab) {
    const plentySum =
      opsReport?.summaries?.find((s) => s.source === "plenty") || null;
    const amazonSum =
      opsReport?.summaries?.find((s) => s.source === "amazon") || null;
    const plentySeries = (opsReport?.series || []).filter(
      (r) => r.source === "plenty",
    );
    const amazonSeries = (opsReport?.series || []).filter(
      (r) => r.source === "amazon",
    );
    const chartDates = [
      ...new Set([
        ...plentySeries.map((r) => r.eval_ds),
        ...amazonSeries.map((r) => r.eval_ds),
      ]),
    ].sort();
    const plentyByDs = Object.fromEntries(
      plentySeries.map((r) => [r.eval_ds, r]),
    );
    const amazonByDs = Object.fromEntries(
      amazonSeries.map((r) => [r.eval_ds, r]),
    );

    return (
      <DashboardLayout>
        <MDBox>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            mb={2}
            gap={2}
          >
            <Box>
              <MDTypography variant="h4" fontWeight="medium" color="dark">
                Forecast Ops
              </MDTypography>
              <MDTypography variant="body2" color="text">
                Accuracy of past predictions vs actuals (MAPE / MAE). Fill from
                locked-in cron days or a walk-forward backtest / Optuna tune.
              </MDTypography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  setOpsRefresh((n) => n + 1);
                  loadStatus();
                }}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                color="info"
                disabled={jobBusy}
                onClick={handleBacktest}
              >
                Fill MAPE from history
              </Button>
              <Button
                variant="contained"
                color="warning"
                disabled={jobBusy}
                startIcon={jobBusy ? <CircularProgress size={16} /> : null}
                onClick={handleHpo}
              >
                Tune params (Optuna)
              </Button>
            </Stack>
          </Stack>

          <Tabs
            value={platform}
            onChange={(_, v) => setPlatform(v)}
            sx={{ mb: 2 }}
          >
            <Tab value="plenty" label="Plenty" />
            <Tab value="amazon" label="Amazon" />
            <Tab value="oos" label="OOS" />
            <Tab value="ops" label="Ops" />
          </Tabs>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              When are LightGBM parameters tuned?
            </Typography>
            <Typography variant="body2">
              <strong>Not</strong> on the nightly cron by default. Nightly (and
              normal &quot;Run&quot;) only re-fits with whatever is already
              stored in <code>forecast_hpo_params</code>, or LightGBM defaults
              if empty. Click <strong>Tune params (Optuna)</strong> for an
              occasional search. Tuning drops the most recent calendar day by
              default (<strong>exclude recent = 1</strong>) so incomplete orders
              do not enter scoring; set hold-out &gt; 0 for an extra unseen
              check after Optuna. Prefer tuning Plenty and Amazon separately
              when one platform drifts.
            </Typography>
          </Alert>

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Tuning settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Applied to <strong>Tune params</strong> and{" "}
                <strong>Fill MAPE from history</strong>. Walk-forward is the
                time-series form of cross-validation; the hold-out is a final
                unseen check against overfitting.
              </Typography>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ sm: "center" }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Typography variant="body2" sx={{ minWidth: 72 }}>
                    Source
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={tuneSource}
                    onChange={(_, v) => v && setTuneSource(v)}
                  >
                    <ToggleButton value="all">Both</ToggleButton>
                    <ToggleButton value="plenty">Plenty only</ToggleButton>
                    <ToggleButton value="amazon">Amazon only</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                <FormGroup row>
                  {["qty", "orders", "revenue", "profit"].map((m) => (
                    <FormControlLabel
                      key={m}
                      control={
                        <Checkbox
                          size="small"
                          checked={tuneMetrics.includes(m)}
                          onChange={(e) => {
                            setTuneMetrics((prev) => {
                              if (e.target.checked) {
                                return [...prev, m];
                              }
                              const next = prev.filter((x) => x !== m);
                              return next.length ? next : prev;
                            });
                          }}
                        />
                      }
                      label={m}
                    />
                  ))}
                </FormGroup>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <TextField
                    size="small"
                    type="number"
                    label="Trials"
                    value={tuneTrials}
                    onChange={(e) =>
                      setTuneTrials(
                        Math.min(200, Math.max(1, Number(e.target.value) || 1)),
                      )
                    }
                    inputProps={{ min: 1, max: 200 }}
                    sx={{ width: 110 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Folds"
                    value={tuneFolds}
                    onChange={(e) =>
                      setTuneFolds(
                        Math.min(24, Math.max(1, Number(e.target.value) || 1)),
                      )
                    }
                    inputProps={{ min: 1, max: 24 }}
                    sx={{ width: 110 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Step days"
                    value={tuneStepDays}
                    onChange={(e) =>
                      setTuneStepDays(
                        Math.min(90, Math.max(7, Number(e.target.value) || 7)),
                      )
                    }
                    inputProps={{ min: 7, max: 90 }}
                    sx={{ width: 120 }}
                    helperText="Gap between fold origins"
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Exclude recent days"
                    value={tuneExcludeRecent}
                    onChange={(e) =>
                      setTuneExcludeRecent(
                        Math.min(14, Math.max(0, Number(e.target.value) || 0)),
                      )
                    }
                    inputProps={{ min: 0, max: 14 }}
                    sx={{ width: 160 }}
                    helperText="Default 1 (drop today)"
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Hold-out days"
                    value={tuneHoldoutDays}
                    onChange={(e) =>
                      setTuneHoldoutDays(
                        Math.min(90, Math.max(0, Number(e.target.value) || 0)),
                      )
                    }
                    inputProps={{ min: 0, max: 90 }}
                    sx={{ width: 140 }}
                    helperText="0 = off; unseen after Optuna"
                  />
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {opsJobMsg && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              onClose={() => setOpsJobMsg(null)}
            >
              {opsJobMsg}
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              What is MAPE?
            </Typography>
            <Typography variant="body2">
              {opsReport?.mape_explained ||
                "MAPE (Mean Absolute Percentage Error) is the average of |forecast − actual| / |actual| across days, shown as a percent. Lower is better. Days with actual ≈ 0 are skipped for MAPE."}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Example: predicted 100 units, sold 80 → that day is 25% error.
              MAPE averages those daily % errors. Rough guide: under ~20% is
              often usable for platform planning; spikes mean data or demand
              shifted.
            </Typography>
          </Alert>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            mb={2}
            alignItems={{ sm: "center" }}
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              value={opsMetric}
              onChange={(_, v) => v && setOpsMetric(v)}
            >
              <ToggleButton value="qty">Units</ToggleButton>
              <ToggleButton value="orders">Orders</ToggleButton>
              <ToggleButton value="revenue">Revenue</ToggleButton>
              <ToggleButton value="profit">Profit</ToggleButton>
            </ToggleButtonGroup>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Window</InputLabel>
              <Select
                label="Window"
                value={opsDays}
                onChange={(e) => setOpsDays(Number(e.target.value))}
              >
                <MenuItem value={7}>Last 7 days</MenuItem>
                <MenuItem value={14}>Last 14 days</MenuItem>
                <MenuItem value={30}>Last 30 days</MenuItem>
                <MenuItem value={90}>Last 90 days</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {!opsLoading && (
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ sm: "center" }}
                  mb={1}
                  gap={1}
                >
                  <Typography variant="subtitle1">
                    Stored LightGBM params (from Optuna)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    HPO on run: {status?.hpo_on_run ? "env ON" : "off"} · trials{" "}
                    {status?.hpo_trials ?? 30} · folds {status?.hpo_folds ?? 4}{" "}
                    · exclude {status?.hpo_exclude_recent_days ?? 1}d · hold-out{" "}
                    {status?.hpo_holdout_days ?? 0}d · metrics{" "}
                    {status?.hpo_metrics || "qty,orders"}
                  </Typography>
                </Stack>
                {!hpoItems.length ? (
                  <Typography variant="body2" color="text.secondary">
                    None yet — production fits use LightGBM defaults until you
                    run <strong>Tune params (Optuna)</strong>.
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Source</TableCell>
                          <TableCell>Metric</TableCell>
                          <TableCell>Walk-forward MAPE</TableCell>
                          <TableCell>Hold-out MAPE</TableCell>
                          <TableCell>Trials / folds</TableCell>
                          <TableCell>Updated</TableCell>
                          <TableCell>Params</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {hpoItems.map((row) => {
                          const holdout =
                            row.params?.holdout_mape_pct ??
                            row.holdout_mape_pct ??
                            null;
                          const paramsForDisplay = { ...(row.params || {}) };
                          delete paramsForDisplay.holdout_mape_pct;
                          delete paramsForDisplay.exclude_recent_days;
                          delete paramsForDisplay.holdout_days;
                          return (
                            <TableRow key={`${row.source}-${row.metric}`}>
                              <TableCell>{row.source}</TableCell>
                              <TableCell>{row.metric}</TableCell>
                              <TableCell>
                                {row.mape_pct != null
                                  ? `${Number(row.mape_pct).toFixed(1)}%`
                                  : "—"}
                              </TableCell>
                              <TableCell>
                                {holdout != null
                                  ? `${Number(holdout).toFixed(1)}%`
                                  : "—"}
                              </TableCell>
                              <TableCell>
                                {row.n_trials ?? "—"} / {row.n_folds ?? "—"}
                              </TableCell>
                              <TableCell>{formatTs(row.updated_at)}</TableCell>
                              <TableCell>
                                <Box
                                  component="code"
                                  sx={{ fontSize: 11, whiteSpace: "pre-wrap" }}
                                >
                                  {JSON.stringify(paramsForDisplay, null, 0)}
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          )}

          {opsLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : !opsReport?.series?.length ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No accuracy rows yet. Use <strong>Fill MAPE from history</strong>{" "}
              (walk-forward on real orders) or{" "}
              <strong>Tune params (Optuna)</strong>, wait for the job, then
              Refresh. For UI-only demo data you can still seed:
              <Box component="pre" sx={{ mt: 1, mb: 0, fontSize: 12 }}>
                {`cd backend/service_forecast\npython scripts/seed_forecast_accuracy.py`}
              </Box>
            </Alert>
          ) : (
            <>
              <Grid container spacing={2} mb={2}>
                {[plentySum, amazonSum].filter(Boolean).map((s) => (
                  <Grid item xs={12} md={6} key={s.source}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {s.source === "plenty" ? "Plenty" : "Amazon"} ·{" "}
                          {s.metric}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Chip
                            color={
                              s.mape_pct == null
                                ? "default"
                                : s.mape_pct < 15
                                  ? "success"
                                  : s.mape_pct < 30
                                    ? "warning"
                                    : "error"
                            }
                            label={`MAPE ${
                              s.mape_pct != null
                                ? `${s.mape_pct.toFixed(1)}%`
                                : "—"
                            }`}
                          />
                          <Chip
                            variant="outlined"
                            label={`MAE ${
                              s.mae != null ? formatNum(s.mae, 1) : "—"
                            }`}
                          />
                          <Chip
                            color={
                              s.bias == null
                                ? "default"
                                : Math.abs(Number(s.bias)) <
                                    Math.max(1, Number(s.mae || 0) * 0.15)
                                  ? "success"
                                  : s.bias > 0
                                    ? "warning"
                                    : "info"
                            }
                            label={
                              s.bias == null
                                ? "Avg offset —"
                                : `Avg offset ${s.bias > 0 ? "+" : ""}${formatNum(
                                    s.bias,
                                    1,
                                  )}${
                                    s.bias_pct != null
                                      ? ` (${s.bias_pct > 0 ? "+" : ""}${Number(
                                          s.bias_pct,
                                        ).toFixed(1)}%)`
                                      : ""
                                  } · ${
                                    Math.abs(Number(s.bias)) <
                                    Math.max(1, Number(s.mae || 0) * 0.15)
                                      ? "on target"
                                      : s.bias > 0
                                        ? "too high"
                                        : "too low"
                                  }`
                            }
                          />
                          <Chip variant="outlined" label={`${s.n_days} days`} />
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Forecast vs actual (platform)
                  </Typography>
                  {chartDates.length ? (
                    <LineChart
                      height={320}
                      xAxis={[{ data: chartDates, scaleType: "point" }]}
                      series={[
                        {
                          data: chartDates.map(
                            (d) => plentyByDs[d]?.forecast_value ?? null,
                          ),
                          label: "Plenty forecast",
                          showMark: false,
                          color: "#1565c0",
                        },
                        {
                          data: chartDates.map(
                            (d) => plentyByDs[d]?.actual_value ?? null,
                          ),
                          label: "Plenty actual",
                          showMark: false,
                          color: "#2e7d32",
                        },
                        {
                          data: chartDates.map(
                            (d) => amazonByDs[d]?.forecast_value ?? null,
                          ),
                          label: "Amazon forecast",
                          showMark: false,
                          color: "#6a1b9a",
                        },
                        {
                          data: chartDates.map(
                            (d) => amazonByDs[d]?.actual_value ?? null,
                          ),
                          label: "Amazon actual",
                          showMark: false,
                          color: "#ef6c00",
                        },
                      ]}
                      margin={{ left: 50, right: 20, top: 20, bottom: 30 }}
                    />
                  ) : null}
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Daily absolute % error (APE)
                  </Typography>
                  {chartDates.length ? (
                    <LineChart
                      height={260}
                      xAxis={[{ data: chartDates, scaleType: "point" }]}
                      yAxis={[{ min: 0 }]}
                      series={[
                        {
                          data: chartDates.map(
                            (d) => plentyByDs[d]?.ape_pct ?? null,
                          ),
                          label: "Plenty APE %",
                          showMark: false,
                          color: "#1565c0",
                        },
                        {
                          data: chartDates.map(
                            (d) => amazonByDs[d]?.ape_pct ?? null,
                          ),
                          label: "Amazon APE %",
                          showMark: false,
                          color: "#6a1b9a",
                        },
                      ]}
                      margin={{ left: 50, right: 20, top: 20, bottom: 30 }}
                    />
                  ) : null}
                </CardContent>
              </Card>
            </>
          )}
        </MDBox>
      </DashboardLayout>
    );
  }

  if (isOosTab) {
    const graphDates = oosCandidates?.graph?.dates || [];
    const graphCounts = oosCandidates?.graph?.counts || [];
    const dPoints = oosDetail?.points || [];
    // Make the split feel like "half the available space" by using most of the
    // viewport and forcing explicit panel heights.
    const oosSplitHeight = Math.round(viewportH * 0.85);
    // Slightly favor the graph panel so it is clearly "half-ish" but usable.
    const oosGraphH = Math.round(oosSplitHeight * 0.55);
    const oosListH = Math.max(240, oosSplitHeight - oosGraphH);
    const oosLineH = Math.max(320, Math.round(oosGraphH * 0.85));
    const oosSelectedOption =
      oosSuggestions?.find((s) => s.product_key === oosSelectedKey) || null;
    const oosAutocompleteValue =
      oosQuery.trim() === oosSelectedKey.trim() ? oosSelectedOption : null;
    return (
      <DashboardLayout>
        <MDBox>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            mb={2}
            gap={2}
          >
            <Box>
              <MDTypography variant="h4" fontWeight="medium" color="dark">
                OOS Explorer
              </MDTypography>
              <MDTypography variant="body2" color="text">
                Project when a Plenty/Amazon product key will run out of stock
                within the forecast horizon.
              </MDTypography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => loadStatus()}
              >
                Refresh status
              </Button>
              <Button
                variant="contained"
                color="info"
                startIcon={jobBusy ? <CircularProgress size={16} /> : null}
                disabled={jobBusy}
                onClick={() => handleRun("both")}
              >
                Run both
              </Button>
            </Stack>
          </Stack>

          <Tabs
            value={platform}
            onChange={(_, v) => setPlatform(v)}
            sx={{ mb: 2 }}
          >
            <Tab value="plenty" label="Plenty" />
            <Tab value="amazon" label="Amazon" />
            <Tab value="oos" label="OOS" />
            <Tab value="ops" label="Ops" />
          </Tabs>

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ md: "center" }}
                justifyContent="space-between"
                flexWrap="wrap"
              >
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={oosSource}
                  onChange={(_, v) => v && setOosSource(v)}
                >
                  <ToggleButton value="plenty">Plenty</ToggleButton>
                  <ToggleButton value="amazon">Amazon</ToggleButton>
                  <ToggleButton value="both">Both</ToggleButton>
                </ToggleButtonGroup>

                <Autocomplete
                  freeSolo
                  options={oosSuggestions}
                  value={oosAutocompleteValue}
                  onInputChange={(_, v) => setOosQuery(v)}
                  onChange={(_, v) => {
                    const k =
                      typeof v === "string"
                        ? v
                        : v?.product_key
                          ? v.product_key
                          : "";
                    setOosSelectedKey(k);
                    setOosQuery(k);
                  }}
                  getOptionLabel={(opt) =>
                    typeof opt === "string" ? opt : opt?.product_key || ""
                  }
                  inputValue={oosQuery}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="ASIN / SKU (product_key)"
                      sx={{ minWidth: 1140 }}
                      placeholder="Start typing e.g. ArabiyatPrestigeMarwaEdP100ml"
                    />
                  )}
                />

                <Button
                  variant="outlined"
                  size="small"
                  sx={{ whiteSpace: "nowrap" }}
                  disabled={oosQuery.trim().length < 1}
                  onClick={() => {
                    const k = oosQuery.trim();
                    if (!k) return;
                    setOosSelectedKey(k);
                  }}
                >
                  Project key
                </Button>
              </Stack>

              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Suggestions come from forecast product keys + current stock in{" "}
                <code>repricer_articles</code>. The table shows only items that
                have forecast demand for the selected Plenty/Amazon mode (with
                `oos_date = null` meaning it does not go OOS within the
                horizon).
              </Typography>
            </CardContent>
          </Card>

          <Grid container spacing={2}>
            <Grid item xs={12} md={7} sx={{ display: "none" }}>
              <Box
                sx={{
                  width: "100%",
                  minHeight: 340,
                  bgcolor: "background.paper",
                  borderRadius: 2,
                  p: 2,
                }}
              >
                {dPoints?.length ? (
                  <LineChart
                    height={320}
                    xAxis={[
                      { data: dPoints.map((p) => p.ds), scaleType: "point" },
                    ]}
                    series={[
                      {
                        data: dPoints.map((p) =>
                          Number(p.remaining_stock ?? 0),
                        ),
                        label: "Remaining stock",
                        showMark: false,
                        color: "#2e7d32",
                      },
                      {
                        data: dPoints.map((p) => Number(p.forecast_qty ?? 0)),
                        label: "Demand (forecast)",
                        showMark: false,
                        color: "#1565c0",
                      },
                    ]}
                    margin={{ left: 60, right: 20, top: 30, bottom: 40 }}
                  />
                ) : (
                  <Typography
                    sx={{ py: 6, textAlign: "center" }}
                    color="text.secondary"
                  >
                    Click <strong>Project key</strong> (or pick an exact
                    suggestion) to see stock/unit changes over time for this
                    ASIN/SKU.
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={7}>
              <Box
                sx={{
                  width: "100%",
                  minWidth: 920,
                  maxWidth: 1200,
                  minHeight: 460,
                  maxHeight: 800,
                  bgcolor: "background.paper",
                  borderRadius: 2,
                  p: 2,
                }}
              >
                {oosDetailLoading ? (
                  <Box display="flex" justifyContent="center" py={6}>
                    <CircularProgress />
                  </Box>
                ) : !oosDetail?.article ? (
                  <Typography
                    sx={{ py: 6, textAlign: "center" }}
                    color="text.secondary"
                  >
                    Pick a suggestion (or type an exact key) to see a per-item
                    OOS projection.
                  </Typography>
                ) : (
                  (() => {
                    const actualSales = oosDetail?.actual_sales || [];
                    const totalActual30d = actualSales.reduce(
                      (s, p) => s + Number(p.actual_qty ?? 0),
                      0,
                    );
                    const allForecastVals = dPoints.flatMap((p) => [
                      Number(p.remaining_stock ?? 0),
                      Number(p.forecast_qty ?? 0),
                    ]);
                    const allActualVals = actualSales.map((p) =>
                      Number(p.actual_qty ?? 0),
                    );
                    const sharedMax = Math.max(
                      0,
                      ...allForecastVals,
                      ...allActualVals,
                    );
                    const yMax =
                      sharedMax > 0 ? Math.ceil(sharedMax * 1.1) : 10;

                    return (
                      <>
                        <Stack
                          direction="row"
                          spacing={1}
                          flexWrap="wrap"
                          mb={1}
                        >
                          <Chip
                            size="small"
                            label={`Matched via ${oosDetail.article.matched_via}`}
                            variant="outlined"
                          />
                          <Chip
                            size="small"
                            color={
                              (oosDetail.starting_stock || 0) > 0
                                ? "success"
                                : "default"
                            }
                            label={`Stock ${formatNum(
                              oosDetail.starting_stock,
                              0,
                            )}`}
                          />
                          {oosDetail.oos_date ||
                          oosDetail.days_until_oos_min != null ? (
                            <Chip
                              size="small"
                              color="error"
                              label={
                                formatOosChipLabel(oosDetail) ||
                                `OOS ${oosDetail.oos_date}`
                              }
                            />
                          ) : oosDetail.covers_horizon ? (
                            <Chip
                              size="small"
                              color="success"
                              label="Covers full horizon"
                            />
                          ) : null}
                          <Chip
                            size="small"
                            color="secondary"
                            variant="outlined"
                            label={`Sold 30d ${formatNum(totalActual30d, 0)}`}
                          />
                        </Stack>

                        <Box
                          sx={{
                            width: "100%",
                            minHeight: 400,
                            bgcolor: "background.default",
                            borderRadius: 2,
                            p: 1,
                          }}
                        >
                          {dPoints?.length ? (
                            <LineChart
                              height={400}
                              xAxis={[
                                {
                                  data: dPoints.map((p) => p.ds),
                                  scaleType: "point",
                                },
                              ]}
                              yAxis={[{ max: yMax, min: 0 }]}
                              series={[
                                {
                                  data: dPoints.map((p) =>
                                    Number(p.remaining_stock ?? 0),
                                  ),
                                  label: "Remaining stock",
                                  showMark: false,
                                  color: "#2e7d32",
                                },
                                {
                                  data: dPoints.map((p) =>
                                    Number(p.forecast_qty ?? 0),
                                  ),
                                  label: "Demand (forecast)",
                                  showMark: false,
                                  color: "#1565c0",
                                },
                              ]}
                              margin={{
                                left: 50,
                                right: 20,
                                top: 20,
                                bottom: 30,
                              }}
                            />
                          ) : (
                            <Typography
                              color="text.secondary"
                              sx={{ py: 6, textAlign: "center" }}
                            >
                              No future demand rows for this key.
                            </Typography>
                          )}
                        </Box>

                        <Box
                          sx={{
                            width: "100%",
                            minHeight: 260,
                            bgcolor: "background.default",
                            borderRadius: 2,
                            p: 1,
                            mt: 1,
                          }}
                        >
                          {actualSales.length ? (
                            <>
                              <Typography
                                variant="subtitle2"
                                sx={{ px: 1, py: 0.5 }}
                              >
                                Actual sales (last 30d)
                              </Typography>
                              <LineChart
                                height={220}
                                xAxis={[
                                  {
                                    data: actualSales.map((p) => p.ds),
                                    scaleType: "point",
                                  },
                                ]}
                                yAxis={[{ max: yMax, min: 0 }]}
                                series={[
                                  {
                                    data: actualSales.map((p) =>
                                      Number(p.actual_qty ?? 0),
                                    ),
                                    label: "Actual units",
                                    showMark: false,
                                    color: "#6a1b9a",
                                  },
                                ]}
                                margin={{
                                  left: 50,
                                  right: 20,
                                  top: 20,
                                  bottom: 30,
                                }}
                              />
                            </>
                          ) : (
                            <Typography
                              color="text.secondary"
                              sx={{ py: 6, textAlign: "center" }}
                            >
                              No order history rows for this key (last 30d).
                            </Typography>
                          )}
                        </Box>
                      </>
                    );
                  })()
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
              <Typography variant="h6" fontWeight="medium" mb={1}>
                OOS projection list (earliest first; includes covers-horizon)
              </Typography>
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 2,
                  maxHeight: 540,
                  overflow: "auto",
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>product_key</TableCell>
                      <TableCell align="right">stock</TableCell>
                      <TableCell align="right">oos range</TableCell>
                      <TableCell align="right">days</TableCell>
                      <TableCell>articlename</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {oosCandidates?.items?.length ? (
                      oosCandidates.items.map((it) => (
                        <TableRow
                          key={`${it.source || ""}:${it.product_key}`}
                          hover
                          onClick={() => {
                            const k = it.product_key;
                            if (!k) return;
                            setOosSelectedKey(k);
                            setOosQuery(k);
                          }}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell sx={{ maxWidth: 260 }}>
                            {it.product_key}
                          </TableCell>
                          <TableCell align="right">
                            {it.stock != null ? formatNum(it.stock, 0) : "—"}
                          </TableCell>
                          <TableCell align="right">
                            {it.oos_date_min &&
                            it.oos_date_max &&
                            it.oos_date_min !== it.oos_date_max
                              ? `${it.oos_date_min} … ${it.oos_date_max}`
                              : (it.oos_date ?? "—")}
                          </TableCell>
                          <TableCell align="right">
                            {formatOosDaysRange(it) ?? "—"}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 360 }}>
                            {it.articlename || "—"}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No forecast product keys found for this prefix/mode.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <MDBox>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          mb={2}
          gap={2}
        >
          <Box>
            <MDTypography variant="h4" fontWeight="medium" color="dark">
              Sales Forecast
            </MDTypography>
            <MDTypography variant="body2" color="text">
              30-day hierarchical forecasts for Plenty and Amazon (CPU
              LightGBM). Platform totals are first-class series — not a SKU
              rollup.
            </MDTypography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                loadStatus();
                loadDimensions();
                loadForecast();
                loadSeriesOptions();
              }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              color="info"
              startIcon={
                jobBusy ? <CircularProgress size={16} /> : <PlayArrowIcon />
              }
              disabled={jobBusy}
              onClick={() => handleRun(platform)}
            >
              Run {platform}
            </Button>
            <Button
              variant="outlined"
              color="info"
              disabled={jobBusy}
              onClick={() => handleRun("all")}
            >
              Run both
            </Button>
            <Button
              variant="outlined"
              color="warning"
              disabled={jobBusy}
              onClick={() => handleRun("all", { hpo: true })}
              title="Optuna tune then forecast — slow; prefer Ops tab for HPO-only"
            >
              Run both + HPO
            </Button>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {String(error)}
          </Alert>
        )}
        {opsJobMsg && platform !== "ops" && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setOpsJobMsg(null)}
          >
            {opsJobMsg}
          </Alert>
        )}

        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <Typography variant="subtitle2">Plenty</Typography>
                  <Chip
                    size="small"
                    label={status?.sources?.plenty?.status || "idle"}
                    color={statusColor(status?.sources?.plenty?.status)}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {status?.sources?.plenty?.message || "No run yet"}
                </Typography>
                <Typography variant="caption" display="block" mt={1}>
                  Series: {status?.sources?.plenty?.series_count ?? "—"} · Last
                  write: {status?.sources?.plenty?.rows_written ?? "—"} rows
                </Typography>
                <Typography variant="caption" display="block">
                  Locked-in past days:{" "}
                  {status?.sources?.plenty?.locked_in_days ?? "—"} (
                  {status?.sources?.plenty?.locked_in_rows ?? "—"} rows, keep{" "}
                  {status?.retain_history_days ??
                    status?.sources?.plenty?.retain_history_days ??
                    365}
                  d)
                </Typography>
                <Typography variant="caption" display="block">
                  Finished: {formatTs(status?.sources?.plenty?.finished_at)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <Typography variant="subtitle2">Amazon</Typography>
                  <Chip
                    size="small"
                    label={status?.sources?.amazon?.status || "idle"}
                    color={statusColor(status?.sources?.amazon?.status)}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {status?.sources?.amazon?.message || "No run yet"}
                </Typography>
                <Typography variant="caption" display="block" mt={1}>
                  Series: {status?.sources?.amazon?.series_count ?? "—"} · Last
                  write: {status?.sources?.amazon?.rows_written ?? "—"} rows
                </Typography>
                <Typography variant="caption" display="block">
                  Locked-in past days:{" "}
                  {status?.sources?.amazon?.locked_in_days ?? "—"} (
                  {status?.sources?.amazon?.locked_in_rows ?? "—"} rows, keep{" "}
                  {status?.retain_history_days ??
                    status?.sources?.amazon?.retain_history_days ??
                    365}
                  d)
                </Typography>
                <Typography variant="caption" display="block">
                  Finished: {formatTs(status?.sources?.amazon?.finished_at)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <TimelineIcon fontSize="small" />
                  <Typography variant="subtitle2">Model config</Typography>
                  {status?.running && (
                    <Chip size="small" color="warning" label="running" />
                  )}
                </Stack>
                <Typography variant="caption" display="block">
                  Horizon: {status?.horizon_days ?? 30}d · Min history:{" "}
                  {status?.min_history_days ?? 30}d
                </Typography>
                <Typography variant="caption" display="block">
                  Product series cap:{" "}
                  {status?.max_product_series === 0 ||
                  status?.max_product_series == null
                    ? "unlimited"
                    : status.max_product_series}{" "}
                  · Threads: {status?.n_jobs ?? "—"}
                </Typography>
                <Typography variant="caption" display="block">
                  Cron: {status?.cron || "—"} (
                  {status?.auto_start_cron ? "on" : "off"})
                </Typography>
                <Typography variant="caption" display="block">
                  Retention: keep past <code>ds</code> for vs-actual; refresh
                  future horizon; prune &gt;{" "}
                  {status?.retain_history_days ?? 365}d
                </Typography>
                <Typography variant="caption" display="block" mt={1}>
                  Optuna HPO:{" "}
                  {status?.hpo_on_run
                    ? "runs before each forecast (env)"
                    : "manual only (Ops tab)"}{" "}
                  · {status?.hpo_trials ?? 30} trials · {status?.hpo_folds ?? 4}{" "}
                  folds · metrics {status?.hpo_metrics || "qty,orders"}
                </Typography>
                <Button
                  size="small"
                  sx={{ mt: 1 }}
                  onClick={() => setPlatform("ops")}
                >
                  Open Ops / tune params
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Tabs
          value={platform}
          onChange={(_, v) => setPlatform(v)}
          sx={{ mb: 2 }}
        >
          <Tab value="plenty" label="Plenty" />
          <Tab value="amazon" label="Amazon" />
          <Tab value="oos" label="OOS" />
          <Tab value="ops" label="Ops" />
        </Tabs>

        {seriesHelp && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {seriesHelp.title}
            </Typography>
            <Typography variant="body2">{seriesHelp.body}</Typography>
            {granularity === "product" && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>product_key</strong> is the database key: Plenty{" "}
                <code>asin_sku</code>, Amazon <code>asin</code> (or{" "}
                <code>seller_sku</code> if ASIN is empty). Search that value
                below — titles are display-only.
              </Typography>
            )}
          </Alert>
        )}

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ md: "center" }}
          mb={2}
          flexWrap="wrap"
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            value={metric}
            onChange={(_, v) => v && setMetric(v)}
          >
            {METRICS.map((m) => (
              <ToggleButton key={m.key} value={m.key}>
                {m.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Series type</InputLabel>
            <Select
              label="Series type"
              value={granularity}
              onChange={(e) => {
                const g = e.target.value;
                setGranularity(g);
                if (g === "platform") setUniqueId(PLATFORM_IDS[platform]);
                else setUniqueId("");
              }}
            >
              {grainChoices.map((g) => (
                <MenuItem key={g.value} value={g.value}>
                  {g.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {platform === "plenty" &&
            (granularity === "channel" || granularity === "product") && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Origin</InputLabel>
                <Select
                  label="Origin"
                  value={
                    granularity === "channel"
                      ? channelFilter || ""
                      : channelFilter
                  }
                  onChange={(e) => onPickOrigin(e.target.value)}
                >
                  {granularity === "product" && (
                    <MenuItem value="">All origins</MenuItem>
                  )}
                  {granularity === "channel" && (
                    <MenuItem value="" disabled>
                      Select origin…
                    </MenuItem>
                  )}
                  {dimensions.plenty_channels.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

          {platform === "plenty" && granularity === "product" && (
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Country</InputLabel>
              <Select
                label="Country"
                value={countryFilter}
                onChange={(e) => {
                  setCountryFilter(e.target.value);
                  setUniqueId("");
                }}
              >
                <MenuItem value="">All countries</MenuItem>
                {dimensions.plenty_countries.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {platform === "amazon" &&
            (granularity === "marketplace" || granularity === "product") && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Marketplace</InputLabel>
                <Select
                  label="Marketplace"
                  value={marketplaceFilter || ""}
                  onChange={(e) => onPickMarketplace(e.target.value)}
                >
                  {granularity === "product" && (
                    <MenuItem value="">All marketplaces</MenuItem>
                  )}
                  {granularity === "marketplace" && (
                    <MenuItem value="" disabled>
                      Select marketplace…
                    </MenuItem>
                  )}
                  {dimensions.amazon_marketplaces.map((m) => (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

          {granularity === "product" && (
            <TextField
              size="small"
              label="ASIN / SKU"
              placeholder="product_key"
              helperText="Exact or partial product_key"
              value={productSearch}
              onChange={(e) => {
                setProductSearch(e.target.value.trimStart());
                setUniqueId("");
              }}
              sx={{ minWidth: 200 }}
            />
          )}

          {granularity === "product" && (
            <FormControl size="small" sx={{ minWidth: 320 }}>
              <InputLabel>Matching series</InputLabel>
              <Select
                label="Matching series"
                value={uniqueId}
                onChange={(e) => setUniqueId(e.target.value)}
                displayEmpty
              >
                {seriesOptions.length === 0 && (
                  <MenuItem value="" disabled>
                    {productSearch.trim()
                      ? "No series for this ASIN/SKU"
                      : "Enter an ASIN / SKU first"}
                  </MenuItem>
                )}
                {seriesOptions.map((s) => (
                  <MenuItem key={s.unique_id} value={s.unique_id}>
                    {formatSeriesLabel(s, granularity)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>

        <Stack direction="row" spacing={3} mb={1} flexWrap="wrap">
          <Typography variant="body2">
            Σ Units: <strong>{formatNum(totals.qty)}</strong>
          </Typography>
          <Typography variant="body2">
            Σ Orders: <strong>{formatNum(totals.orders)}</strong>
          </Typography>
          <Typography variant="body2">
            Σ Revenue: <strong>{formatNum(totals.revenue)}</strong>
          </Typography>
          <Typography variant="body2">
            Σ Profit: <strong>{formatNum(totals.profit)}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Days: {points.length}
            {dataPointsUsed != null
              ? ` · History days used: ${dataPointsUsed}`
              : ""}
            {modelRunAt ? ` · Model: ${formatTs(modelRunAt)}` : ""}
          </Typography>
        </Stack>

        {(granularity === "platform" || selectedSeries) && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mb={1}
          >
            {granularity === "platform" ? (
              <>
                Series key: <strong>{PLATFORM_IDS[platform]}</strong> ·
                granularity <strong>platform</strong>
              </>
            ) : (
              <>
                Series key: <strong>{selectedSeries.unique_id}</strong>
                {selectedSeries.product_key ? (
                  <>
                    {" "}
                    · product_key <strong>{selectedSeries.product_key}</strong>
                  </>
                ) : null}
                {selectedSeries.channel
                  ? ` · origin ${selectedSeries.channel}`
                  : ""}
                {selectedSeries.country
                  ? ` · country ${selectedSeries.country}`
                  : ""}
                {selectedSeries.marketplace
                  ? ` · marketplace ${selectedSeries.marketplace}`
                  : ""}
              </>
            )}
          </Typography>
        )}

        {otherStatus?.status === "error" && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {otherPlatform} last run failed: {otherStatus.message}
          </Alert>
        )}

        <Box
          sx={{
            width: "100%",
            minHeight: 360,
            bgcolor: "background.paper",
            borderRadius: 2,
            p: 2,
          }}
        >
          {loading ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress />
            </Box>
          ) : chartData.dates.length === 0 ? (
            <Typography
              color="text.secondary"
              sx={{ py: 8, textAlign: "center" }}
            >
              No forecast points for this series.
              {granularity === "channel"
                ? " Select an origin above."
                : granularity === "marketplace"
                  ? " Select a marketplace above."
                  : granularity === "product"
                    ? " Search an ASIN/SKU (product_key)."
                    : ` Use Run ${platform} or Run both.`}
            </Typography>
          ) : (
            <LineChart
              height={340}
              xAxis={[{ data: chartData.dates, scaleType: "point" }]}
              series={[
                {
                  data: chartData.values,
                  label: METRICS.find((m) => m.key === metric)?.label || metric,
                  showMark: false,
                },
              ]}
              margin={{ left: 60, right: 20, top: 30, bottom: 40 }}
            />
          )}
        </Box>

        {granularity === "product" && (uniqueId || productSearch.trim()) && (
          <Box mt={3}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ sm: "center" }}
              gap={2}
              mb={1}
            >
              <Box>
                <MDTypography variant="h6" fontWeight="medium">
                  Stock vs forecast (OOS)
                </MDTypography>
                <Typography variant="body2" color="text.secondary">
                  Remaining stock from <code>repricer_articles.stock</code>,
                  burned down by forecast units. Default demand = all series for
                  this product_key (shared warehouse).
                </Typography>
              </Box>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={stockoutScope}
                onChange={(_, v) => v && setStockoutScope(v)}
              >
                <ToggleButton value="product">All series</ToggleButton>
                <ToggleButton value="series">This series only</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            {stockoutLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={28} />
              </Box>
            ) : !stockout?.article ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                No matching row in <code>repricer_articles</code> for{" "}
                <strong>
                  {selectedSeries?.product_key || productSearch || "—"}
                </strong>{" "}
                (tried sku / FBA asin / seller_sku).
              </Alert>
            ) : (
              <>
                <Stack direction="row" spacing={2} flexWrap="wrap" mb={1}>
                  <Chip
                    size="small"
                    label={`SKU ${stockout.article.sku}`}
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    color={stockout.starting_stock > 0 ? "success" : "default"}
                    label={`Stock ${formatNum(stockout.starting_stock, 0)}`}
                  />
                  <Chip
                    size="small"
                    label={`Matched via ${stockout.article.matched_via}`}
                    variant="outlined"
                  />
                  {stockout.oos_date || stockout.days_until_oos_min != null ? (
                    <Chip
                      size="small"
                      color="error"
                      label={
                        formatOosChipLabel(stockout) ||
                        `OOS ${stockout.oos_date}`
                      }
                    />
                  ) : stockout.covers_horizon ? (
                    <Chip
                      size="small"
                      color="success"
                      label="Covers full 30d horizon"
                    />
                  ) : null}
                </Stack>

                {stockout.oos_date || stockout.days_until_oos_min != null ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    Projected out of stock{" "}
                    {stockout.oos_date_min &&
                    stockout.oos_date_max &&
                    stockout.oos_date_min !== stockout.oos_date_max ? (
                      <>
                        between <strong>{stockout.oos_date_min}</strong> and{" "}
                        <strong>{stockout.oos_date_max}</strong> (
                        {formatOosDaysRange(stockout)})
                      </>
                    ) : (
                      <>
                        on <strong>{stockout.oos_date}</strong> (
                        {formatOosDaysRange(stockout) ||
                          `${stockout.days_until_oos}d`}
                        )
                      </>
                    )}
                    , starting from {formatNum(stockout.starting_stock, 0)}{" "}
                    units
                    {stockout.article.articlename
                      ? ` — ${stockout.article.articlename}`
                      : ""}
                    {stockout.bias?.bias_pct != null
                      ? ` (platform bias ${Number(stockout.bias.bias_pct).toFixed(1)}%)`
                      : " (default ±15% demand band)"}
                    .
                  </Alert>
                ) : stockout.covers_horizon ? (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Current stock covers the full forecast horizon (no OOS in
                    the next {stockoutChart.dates.length || 30} days).
                  </Alert>
                ) : stockout.message ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {stockout.message}
                  </Alert>
                ) : null}

                <Box
                  sx={{
                    width: "100%",
                    minHeight: 320,
                    bgcolor: "background.paper",
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  {stockoutChart.dates.length === 0 ? (
                    <Typography
                      color="text.secondary"
                      sx={{ py: 6, textAlign: "center" }}
                    >
                      No stockout points — need a product forecast on the latest
                      run.
                    </Typography>
                  ) : (
                    <LineChart
                      height={300}
                      xAxis={[
                        { data: stockoutChart.dates, scaleType: "point" },
                      ]}
                      series={[
                        {
                          data: stockoutChart.remaining,
                          label: "Remaining stock",
                          showMark: false,
                          color: stockout.oos_date ? "#d32f2f" : "#2e7d32",
                        },
                        {
                          data: stockoutChart.demand,
                          label: "Forecast units / day",
                          showMark: false,
                          color: "#1976d2",
                        },
                      ]}
                      margin={{ left: 60, right: 20, top: 30, bottom: 40 }}
                    />
                  )}
                </Box>
              </>
            )}
          </Box>
        )}
      </MDBox>
    </DashboardLayout>
  );
};

export default ForecastPage;
